import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  BrandFooter,
  BrandHeader,
  Wordmark,
} from "@/components/runboom/marks";
import { getLines, type PredictionLine } from "@/lib/api";
import { upcomingRaces, heroPrediction } from "@/lib/mock";
import { formatRaceTime } from "@/lib/format";

const DISTANCE_TONE: Record<string, string> = {
  "5K": "var(--color-bone)",
  "10K": "var(--color-acid-lime)",
  Half: "var(--color-lavender)",
  Marathon: "var(--color-electric-blue)",
};

export const Route = createFileRoute("/oracle/")({
  component: OracleIndex,
});

function OracleIndex() {
  const [livesByRace, setLivesByRace] = useState<Record<string, PredictionLine>>({});
  const [source, setSource] = useState<"strava" | "seed">("seed");

  useEffect(() => {
    let cancelled = false;
    getLines()
      .then((lines) => {
        if (cancelled) return;
        const map: Record<string, PredictionLine> = {};
        for (const line of lines) map[line.raceSlug] = line;
        setLivesByRace(map);
        if (lines[0]?.source === "strava") setSource("strava");
      })
      .catch(() => {
        /* unauthed — fall back to seed */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <BrandHeader rightTab="The Oracle" rightTabTone="lime" />

      <div className="mt-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Wordmark size="sm" />
        </Link>
        <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
          <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
          <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
          <Link
            to="/leaderboards"
            search={{ distance: "10K", city: "Cape Town" }}
            className="hidden hover:text-fg sm:inline"
          >
            Leaderboards
          </Link>
          <Link
            to="/me"
            className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: "var(--color-acid-lime)" }}
          >
            Connect Strava
          </Link>
        </nav>
      </div>

      <header className="mt-8">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-blood-orange)" }}>
            Lines on every race in your calendar
          </span>
          {source === "strava" ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-night"
              style={{ backgroundColor: "var(--color-acid-lime)" }}
            >
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-night" />
              Live · from your Strava
            </span>
          ) : null}
        </div>
        <h1 className="mt-3 font-display-italic font-display text-[56px] leading-[48px] tracking-[-0.015em] text-fg sm:text-[88px] sm:leading-[76px] lg:text-[120px] lg:leading-[100px]">
          THE ORACLE.
        </h1>
        <p className="mt-6 max-w-[60ch] font-mono text-[12px] uppercase tracking-[0.12em] text-fg-muted">
          The Oracle scores every upcoming race against your last 90 days of training and a 14-day forecast for the course.
          <span className="ml-1" style={{ color: "var(--color-acid-lime)" }}>Tap a race for the full line.</span>
        </p>
      </header>

      <section className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 md:grid-cols-2 xl:grid-cols-3">
        {upcomingRaces.map((race) => {
          const live = livesByRace[race.slug];
          const p50 = live?.p50Seconds ?? race.yourPredictedSeconds;
          const p10 = live?.p10Seconds ?? Math.max(0, p50 - 85);
          const p90 = live?.p90Seconds ?? p50 + 105;
          const isHero = race.slug === heroPrediction.raceSlug;
          const boom = live?.boomProbabilityPct ?? (isHero ? heroPrediction.boomProbabilityPct : 58);
          const tone = DISTANCE_TONE[race.distanceLabel] ?? "var(--color-fg)";

          return (
            <Link
              key={race.slug}
              to="/oracle/$raceSlug"
              params={{ raceSlug: race.slug }}
              className="group rounded-[18px] border border-border bg-bg-elevated p-6 transition-colors hover:border-acid-lime/40"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
                  {race.distanceLabel} · {new Date(race.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase()}
                </span>
                <span
                  className="rounded-full px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-night"
                  style={{ backgroundColor: "var(--color-blood-orange)" }}
                >
                  BOOM {boom}%
                </span>
              </div>
              <h2 className="mt-4 font-display text-[22px] leading-[24px] tracking-[-0.01em] text-fg">
                {race.name.toUpperCase()}
              </h2>
              <div
                className="mt-6 font-display tabular text-[48px] leading-[48px] tracking-[-0.02em]"
                style={{ color: tone }}
              >
                {formatRaceTime(p50)}
              </div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
                {formatRaceTime(p10)} – {formatRaceTime(p90)}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.1em] text-fg group-hover:text-acid-lime">
                Open the Line
                <span aria-hidden>→</span>
              </div>
            </Link>
          );
        })}
      </section>

      <BrandFooter />
    </main>
  );
}
